/**
 * Подсказка ⓘ к действию (UI-ACTIONS): значок рядом с кнопкой или полем, по нажатию — карточка из реестра действий
 * приложения: что кнопка делает, что изменит, кому доступна, как проверено. Описания приходят через контекст
 * (`actionInfoContext`), ui-kit store не знает. Карточка закрывается по Esc и щелчку мимо; фокус уходит в неё для диктора.
 */
import {
  AppWindow,
  Ban,
  Banknote,
  BadgeCheck,
  Check,
  CheckCheck,
  CircleDashed,
  CircleHelp,
  Database,
  Eye,
  Globe,
  Info,
  Monitor,
  Upload,
  X,
} from 'lucide-react'
import {
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

import './actionInfo.css'
import { ActionInfoContext, ActionInfoQuietContext, textOf, type ActionInfoCard, type ActionInfoEffect, type ActionInfoEvidenceLine, type ActionInfoStatus } from './actionInfoContext'

/**
 * Подсказка ⓘ у кнопки (UI-ACTIONS, решение владельца 09.10.2026): нажал —
 * увидел, что кнопка делает, что изменит, кому доступна, почему сейчас
 * выключена и как проверено, что она работает.
 *
 * ui-kit не знает store (D-052): описание приходит через контекст, который
 * ставит приложение (`ActionInfoContext.Provider`) из реестра действий. Нет
 * провайдера, подсказки выключены или записи нет — ⓘ не рисуется, кнопка
 * работает как раньше: недописанный реестр механизм не ломает.
 */

const EFFECTS: Record<ActionInfoEffect, { word: string; Icon: typeof Info }> = {
  view: { word: 'Только интерфейс: ничего не сохраняет', Icon: AppWindow },
  device: { word: 'Запоминается на этом устройстве', Icon: Monitor },
  read: { word: 'Только смотрит: ничего не меняет', Icon: Eye },
  'read-external': { word: 'Спрашивает внешнюю систему, ничего в ней не меняет', Icon: Globe },
  'write-local': { word: 'Пишет в нашу базу', Icon: Database },
  'write-external': { word: 'Пишет во внешнюю систему', Icon: Upload },
  cash: { word: 'Касса: деньги и чек', Icon: Banknote },
}

const STATUSES: Record<ActionInfoStatus, { word: string; Icon: typeof Info }> = {
  live: { word: 'Проверено вживую', Icon: BadgeCheck },
  'stand-and-test': { word: 'Проверено тестом и на стенде', Icon: CheckCheck },
  'test-only': { word: 'Проверено только тестом', Icon: Check },
  unverified: { word: 'Не проверено', Icon: CircleDashed },
  stub: { word: 'Не работает', Icon: Ban },
  unclear: { word: 'Назначение уточняется', Icon: CircleHelp },
}

const EVIDENCE: Record<ActionInfoEvidenceLine['kind'], string> = {
  live: 'Вживую',
  stand: 'На стенде',
  unit: 'Тест экрана',
  server: 'Тест сервера',
}

/** Имя теста — утверждение словами через «_» (CLAUDE.md §1): в карточке — через пробелы, читается как фраза. */
const readable = (title: string) => title.replaceAll('_', ' ')

interface CardState {
  card: ActionInfoCard | undefined
}

/** Что рисовать у элемента: `null` — ничего (подсказки выключены или запись не нужна). */
function useActionCard(id: string): CardState | null {
  const source = useContext(ActionInfoContext)
  const quiet = useContext(ActionInfoQuietContext)
  if (quiet || !source.enabled) return null
  const card = source.describe(id)
  if (card === undefined && !source.audit) return null
  return { card }
}

/** Область экрана без ⓘ у отдельных кнопок: справка на таком экране одна, сверху слева (`ScreenInfo`). */
export function ActionInfoQuiet({ children }: { children: ReactNode }) {
  return <ActionInfoQuietContext.Provider value={true}>{children}</ActionInfoQuietContext.Provider>
}

function MissingMark({ id }: { id: string }) {
  return (
    <span className="krp-action-missing" title={id}>
      <CircleDashed size={12} aria-hidden="true" />
      нет описания
    </span>
  )
}

interface InfoProps {
  card: ActionInfoCard
  /** Что написано на элементе — для диктора и заголовка карточки. */
  label: string
  disabled: boolean
  disabledReason: string | null
}

/** Отдельная кнопка ⓘ рядом с элементом (вложенные кнопки недопустимы) и непомодальная карточка. */
function InfoButton({ card, label, disabled, disabledReason }: InfoProps) {
  // Куда рисовать карточку: в открытое модальное окно, если ⓘ в нём, — иначе диктор её не прочтёт
  // (всё вне `aria-modal` для него неактивно); иначе — в body. `null` — карточка закрыта.
  const [host, setHost] = useState<Element | null>(null)
  const open = host !== null
  const button = useRef<HTMLButtonElement>(null)
  const cardId = useId()
  return (
    <>
      <button
        ref={button}
        type="button"
        className="krp-action-info"
        aria-label={`Что делает «${label}»`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? cardId : undefined}
        data-status={card.status}
        onClick={(event) => {
          // Внутри <summary> щелчок не раскрывает список, внутри строки — не нажимает строку.
          event.preventDefault()
          event.stopPropagation()
          const dialog = event.currentTarget.closest('[role="dialog"][aria-modal="true"]') ?? document.body
          setHost((current) => (current !== null ? null : dialog))
        }}
      >
        <Info size={14} aria-hidden="true" />
      </button>
      {host !== null && (
        <InfoCard
          id={cardId}
          host={host}
          anchor={button}
          card={card}
          label={label}
          disabled={disabled}
          disabledReason={disabledReason}
          onClose={(refocus) => {
            setHost(null)
            if (refocus) button.current?.focus()
          }}
        />
      )}
    </>
  )
}

const MARGIN = 8
const GAP = 6
/** Меньше — карточку не ужимать: пусть лучше перекроет часть экрана, чем станет щелью. */
const MIN_ROOM = 160
/** Уже — карточка становится нижним листом во всю ширину (телефон). */
const SHEET_BELOW = 560

function InfoCard({
  id,
  host,
  anchor,
  card,
  label,
  disabled,
  disabledReason,
  onClose,
}: InfoProps & {
  id: string
  host: Element
  anchor: RefObject<HTMLButtonElement | null>
  onClose: (refocus: boolean) => void
}) {
  const titleId = useId()
  const node = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  useEffect(() => {
    close.current = onClose
  }, [onClose])

  // Рядом с ⓘ, не выходя за экран: снизу, если помещается, иначе где места больше — и не выше этого места
  // (длинная карточка прокручивается внутри). На узком экране — нижним листом.
  useLayoutEffect(() => {
    const place = () => {
      const element = node.current
      const target = anchor.current
      if (element === null || target === null) return
      element.style.removeProperty('max-height')
      if (window.innerWidth < SHEET_BELOW) {
        element.dataset.placement = 'sheet'
        element.style.removeProperty('top')
        element.style.removeProperty('left')
        return
      }
      element.dataset.placement = 'popover'
      const rect = target.getBoundingClientRect()
      const width = element.offsetWidth
      const below = window.innerHeight - rect.bottom - GAP - MARGIN
      const above = rect.top - GAP - MARGIN
      const downward = element.offsetHeight <= below || below >= above
      const room = Math.max(MIN_ROOM, downward ? below : above)
      if (element.offsetHeight > room) element.style.maxHeight = `${room}px`
      const height = element.offsetHeight
      const top = downward ? rect.bottom + GAP : rect.top - GAP - height
      const left = Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - MARGIN)
      element.style.top = `${Math.max(MARGIN, top)}px`
      element.style.left = `${Math.max(MARGIN, left)}px`
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [anchor])

  // Фокус — в карточку (диктор читает её), Esc и щелчок мимо закрывают. Esc ловится раньше всех:
  // модальное окно под подсказкой от него не закрывается.
  useEffect(() => {
    node.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      close.current(true)
    }
    const onPointer = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && (node.current?.contains(target) === true || anchor.current?.contains(target) === true)) return
      close.current(false)
    }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('pointerdown', onPointer, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('pointerdown', onPointer, true)
    }
  }, [anchor])

  const effect = EFFECTS[card.effect]
  const status = STATUSES[card.status]
  const reasonNow = disabled ? (disabledReason ?? card.disabledWhen ?? 'Причину экран не назвал.') : null
  return createPortal(
    <div
      ref={node}
      id={id}
      className="krp-action-card"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <div className="krp-action-card__head">
        <p className="krp-action-card__title" id={titleId}>
          {label}
        </p>
        <button type="button" className="krp-action-card__close" aria-label="Закрыть подсказку" onClick={() => onClose(true)}>
          <X size={14} aria-hidden="true" />
        </button>
      </div>
      <p className="krp-action-card__summary">{card.summary}</p>
      <dl className="krp-action-card__facts">
        <dt>Что изменит</dt>
        <dd>
          <span className="krp-action-card__effect" data-effect={card.effect}>
            <effect.Icon size={14} aria-hidden="true" />
            {effect.word}
          </span>
          {card.effectText !== null && <span className="krp-action-card__note">{card.effectText}</span>}
        </dd>
        {card.access !== null && (
          <>
            <dt>Кому доступна</dt>
            <dd>{card.access}</dd>
          </>
        )}
        {reasonNow !== null ? (
          <>
            <dt>Сейчас недоступна</dt>
            <dd className="krp-action-card__reason">{reasonNow}</dd>
          </>
        ) : (
          card.disabledWhen !== null && (
            <>
              <dt>Когда недоступна</dt>
              <dd>{card.disabledWhen}</dd>
            </>
          )
        )}
        {card.gestureOf !== null && (
          <>
            <dt>То же жестом</dt>
            <dd>{card.gestureOf}</dd>
          </>
        )}
        <dt>Как проверено</dt>
        <dd>
          <span className="krp-action-card__status" data-status={card.status}>
            <status.Icon size={14} aria-hidden="true" />
            {status.word}
          </span>
          {card.statusReason !== null && <span className="krp-action-card__note">{card.statusReason}</span>}
        </dd>
      </dl>
      {card.evidence.length > 0 && (
        <ul className="krp-action-card__evidence" aria-label="Чем проверено">
          {card.evidence.map((line) => (
            <li key={`${line.kind}:${line.where}:${line.title}`}>
              <span className="krp-action-card__kind">{EVIDENCE[line.kind]}:</span> {readable(line.title)}{' '}
              <span className="krp-action-card__where">{line.where}</span>
            </li>
          ))}
        </ul>
      )}
    </div>,
    host,
  )
}

export interface ActionInfoProps {
  /** Запись реестра действий: `platform.sync-rule.enable`. */
  id: string
  /** Что написано на элементе. Нет — подпись из реестра. */
  label?: string
  /** Элемент сейчас выключен: карточка говорит почему (у выключенного ⓘ остаётся активной). */
  disabled?: boolean
  disabledReason?: string | null
  /**
   * Где ⓘ: `inline` — рядом в одной строке (обёртка `span`), `block` — строка во всю
   * ширину, элемент растягивается (пункт меню, плитка), `sibling` — без обёртки, ⓘ
   * следующим элементом родителя (полоса вкладок, шапка).
   */
  layout?: 'inline' | 'block' | 'sibling'
  children?: ReactNode
}

/**
 * ⓘ для элемента, который сам `actionId` не принимает: ссылка, пункт меню,
 * полоса вкладок, `<summary>`, радиокнопки. Без детей — одна ⓘ (внутри `<summary>`).
 */
export function ActionInfo({ id, label, disabled = false, disabledReason = null, layout = 'inline', children }: ActionInfoProps) {
  const state = useActionCard(id)
  if (state === null) return <>{children}</>
  const info =
    state.card === undefined ? (
      <MissingMark id={id} />
    ) : (
      <InfoButton card={state.card} label={label ?? state.card.label} disabled={disabled} disabledReason={disabledReason} />
    )
  if (children === undefined) return info
  if (layout === 'sibling') {
    return (
      <>
        {children}
        {info}
      </>
    )
  }
  if (layout === 'block') {
    return (
      <div className="krp-action" data-layout="block" data-missing={state.card === undefined || undefined}>
        {children}
        {info}
      </div>
    )
  }
  return (
    <span className="krp-action" data-missing={state.card === undefined || undefined}>
      {children}
      {info}
    </span>
  )
}

/**
 * Группа выбора (радиокнопки, галочки одного решения) с ⓘ у подписи группы.
 * ⓘ — рядом с `<legend>`, а не внутри: иначе имя группы для диктора включало бы «Что делает…».
 */
export function ActionFieldset({
  actionId,
  legend,
  className,
  disabled = false,
  disabledReason = null,
  children,
}: {
  actionId: string
  legend: ReactNode
  className?: string
  disabled?: boolean
  disabledReason?: string | null
  children: ReactNode
}) {
  const state = useActionCard(actionId)
  const label = textOf(legend)
  return (
    <fieldset className={className}>
      <legend>{legend}</legend>
      {state !== null && (
        <span className="krp-action-legend">
          {state.card === undefined ? (
            <MissingMark id={actionId} />
          ) : (
            <InfoButton
              card={state.card}
              label={label === undefined || label === '' ? state.card.label : label}
              disabled={disabled}
              disabledReason={disabledReason}
            />
          )}
        </span>
      )}
      {children}
    </fieldset>
  )
}
